import React, { Fragment, ReactNode, useEffect } from "react";

import { startCase } from "lodash";

import { IconNames } from "@src/straps/base/icons/Icon/Icon";

import { useControllableState, usePrevious } from "@src/global_functions/hooks";
import { getObjectEntries, groupBy } from "@src/global_functions/util";
import classNames from "classnames";
import useSubscription from "../../../../global_functions/hooks/useSubscription";
import DropdownWrapper from "../../../utils/DropdownWrapper/DropdownWrapper";
import Text, { TextVariants } from "../../type/Text/Text";
import { DropdownOptionButton } from "./DropdownOptionButton";
import DropdownTarget from "./DropdownTarget";
import DropdownMultiple from "./variants/DropdownMultiple";
import DropdownMultipleSearch from "./variants/DropdownMultipleSearch";
import DropdownSearch from "./variants/DropdownSearch";

type MultipleOptionDropdownVariant = "multiple" | "multipleSearch";
type SingleOptionDropdownVariant = "normal" | "largeSearch" | "normalSearch";
export type DropdownVariant =
  | SingleOptionDropdownVariant
  | MultipleOptionDropdownVariant;

type SearchVariant = Exclude<DropdownVariant, "normal" | "multiple">;

type DropdownStatus = "default" | "negative" | "positive" | "warning";
type DropdownTint = "default" | "darker" | "brand";

export const isMultipleOptionVariant = (
  variant: DropdownVariant
): variant is MultipleOptionDropdownVariant =>
  ["multiple", "multipleSearch"].includes(variant);

const isSingleOptionVariant = (
  variant: DropdownVariant
): variant is SingleOptionDropdownVariant =>
  ["normal", "largeSearch", "normalSearch"].includes(variant);

const isSearchVariant = (variant: DropdownVariant): variant is SearchVariant =>
  (
    ["multipleSearch", "largeSearch", "normalSearch"] satisfies SearchVariant[]
  ).some((searchVariant) => searchVariant === variant);

export type ButtonTextSizeOptions = "small" | "base" | "xs";

export interface BaseOption<IDType> {
  id: IDType;
  label?: string;
  icon?: IconNames;
  disabled?: boolean;
  nodeLabel?: React.ReactNode;
  supportText?: string;
  new?: boolean;
  backgroundColor?: string;
  imageURL?: string;
}

type OptionWithMetadata<IDType, Metadata> = BaseOption<IDType> & Metadata;

export type Option<
  IDType,
  OptionMetadata extends {} | void = void
> = OptionMetadata extends void
  ? BaseOption<IDType>
  : OptionWithMetadata<IDType, OptionMetadata>;

export type DropdownOptions<
  OptionIDType,
  OptionMetadata extends {} | void = void
> =
  | Option<OptionIDType, OptionMetadata>
  | Array<Option<OptionIDType, OptionMetadata>>
  | null
  | undefined;

export type DropdownProps<OptionIDType, OptionMetadata extends {} | void> = {
  readonly className?: string;
  readonly variant?: DropdownVariant;
  readonly disabled?: boolean;
  readonly placeholder?: string;
  readonly options: Array<Option<OptionIDType, OptionMetadata>>;
  readonly label?: string;
  readonly labelTextVariant?: TextVariants;
  readonly name?: string;
  readonly onSelect?: (
    newValue?: DropdownOptions<OptionIDType, OptionMetadata>
  ) => void;
  readonly onCancel?: (
    prev?: DropdownOptions<OptionIDType, OptionMetadata>
  ) => void;
  readonly dataTestId: string;
  readonly maxHeight?: number;
  readonly positionOptionsRelative?: boolean;
  readonly positionOptionsRelativeHeight?: number; // prevent scroll position jumping when multiple search variant is filtered
  readonly selectOnCheck?: boolean;
  readonly onApply?: (
    options: DropdownOptions<OptionIDType, OptionMetadata>
  ) => void;
  readonly applyText?: string;
  readonly groupBy?: {
    key: keyof Option<OptionIDType, OptionMetadata>;
    labelKey?: keyof Option<OptionIDType, OptionMetadata>;
  };
  readonly status?: DropdownStatus;
  readonly tint?: DropdownTint;
  readonly supportText?: string;
  readonly supportTextVariant?: TextVariants;
  readonly buttonTextSize?: ButtonTextSizeOptions;
  readonly extendContainerRefs?: React.RefObject<HTMLElement>[];
  readonly extraDropdownRef?: React.RefObject<HTMLDivElement>;
  readonly onFocus?: () => void;
  readonly onBlur?: () => void;
  readonly controlledValue?: DropdownOptions<OptionIDType, OptionMetadata>;
  readonly defaultValue?: DropdownOptions<OptionIDType, OptionMetadata>;
  readonly emptyOptionsPlaceholder?: ReactNode;
  readonly minWidth?: number;
  readonly as?: React.ReactElement;
  // only implemented in search variants
  readonly numVisibleOptions?: number;
  readonly viewMoreOptionsButtonText?: string;
  // for multi-select: max labels to display before collapsing to "first N, +X more"
  readonly maxDisplayCount?: number;
};

const Dropdown = <
  OptionIDType extends string | number = string,
  OptionMetadata extends {} | void = void
>({
  variant = "normal",
  disabled,
  onSelect,
  label,
  labelTextVariant,
  name,
  options,
  positionOptionsRelative,
  positionOptionsRelativeHeight,
  selectOnCheck,
  applyText,
  placeholder,
  dataTestId,
  maxHeight = 600,
  status = "default",
  tint,
  supportText,
  supportTextVariant,
  buttonTextSize = "base",
  extendContainerRefs,
  extraDropdownRef,
  onFocus,
  onBlur,
  onCancel,
  onApply,
  groupBy: groupByProp,
  emptyOptionsPlaceholder,
  minWidth,
  as,
  numVisibleOptions,
  viewMoreOptionsButtonText,
  maxDisplayCount,
  ...props
}: DropdownProps<OptionIDType, OptionMetadata>) => {
  const [dropdownValue, setUncontrolledState] = useControllableState<
    DropdownOptions<OptionIDType, OptionMetadata>
  >(props.controlledValue, props.defaultValue);

  const prevRef = usePrevious(dropdownValue);

  const setDropdownValue = React.useCallback(
    (newValue: any) => {
      setUncontrolledState(newValue);
      if (typeof newValue === "function") {
        onSelect?.(newValue(prevRef));
      } else {
        onSelect?.(newValue);
      }
    },
    [onSelect, setUncontrolledState, prevRef]
  ) satisfies typeof setUncontrolledState;

  const [closeSubscription, closeEvent] = useSubscription();

  useEffect(() => {
    // close dropdown every time dropdownValue changes for normal dropdown
    if (isSingleOptionVariant(variant)) {
      closeEvent();
    }
  }, [dropdownValue, closeEvent, variant]);

  const dropdownText = React.useMemo(() => {
    if (dropdownValue) {
      if (Array.isArray(dropdownValue)) {
        const labels = dropdownValue.map(
          (option) => option.label || startCase(option.id.toString())
        );
        if (maxDisplayCount !== undefined && labels.length > maxDisplayCount) {
          return `${labels.slice(0, maxDisplayCount).join(", ")} +${
            labels.length - maxDisplayCount
          } more`;
        }
        return labels.join(", ");
      }
      return (
        dropdownValue.nodeLabel ||
        dropdownValue.label ||
        startCase(dropdownValue.id.toString())
      );
    }
    return "";
  }, [dropdownValue, maxDisplayCount]);

  const isChecked = React.useCallback(
    (option: Option<OptionIDType, OptionMetadata>) => {
      if (dropdownValue) {
        if (Array.isArray(dropdownValue)) {
          const selectedIDs = dropdownValue.map((o) => o.id);
          return selectedIDs.includes(option.id);
        } else {
          return dropdownValue.id === option.id && !option.disabled;
        }
      }
      return false;
    },
    [dropdownValue]
  );

  // additional open state tracking for updating styling
  const [menuOpened, setMenuOpened] = React.useState(false);

  const dropdownMultipleValue = React.useMemo(
    () => (Array.isArray(dropdownValue) ? dropdownValue : []),
    [dropdownValue]
  );

  const _placeholder =
    placeholder ?? (isSearchVariant(variant) ? "Search..." : "Select...");

  let supportTextColor: "tertiary" | "negative" | "secondary" = "secondary";
  if (disabled) {
    supportTextColor = "tertiary";
  } else if (status === "negative") {
    supportTextColor = "negative";
  }

  return (
    <div className="w-full">
      {label && (
        <Text
          variant={labelTextVariant ?? "sb_t-14-500"}
          color="secondary"
          className="mb-3"
          data-testid="dropdown-label"
        >
          <label htmlFor={name}>{label}</label>
        </Text>
      )}
      <DropdownWrapper
        maxHeight={maxHeight}
        positionRelativeHeight={positionOptionsRelativeHeight}
        disabled={disabled}
        fullWidth
        minWidth={minWidth}
        allowRenderAbove={!isSearchVariant(variant)}
        positionRelative={positionOptionsRelative}
        closeSubscription={closeSubscription}
        open={menuOpened}
        setOpen={setMenuOpened}
        extraRefs={extendContainerRefs}
        extraDropdownRef={extraDropdownRef}
        innerClassName={classNames({
          "my-0.5": variant !== "largeSearch" && !positionOptionsRelative,
        })}
        as={
          as ?? (
            <DropdownTarget
              disabled={disabled}
              dropdownText={dropdownText}
              variant={variant}
              status={status}
              tint={tint}
              positionOptionsRelative={positionOptionsRelative}
              dropdownValue={dropdownValue}
              dataTestId={dataTestId}
              placeholder={_placeholder}
              buttonTextSize={buttonTextSize}
              menuOpened={menuOpened}
            />
          )
        }
        onFocus={onFocus}
        onBlur={onBlur}
      >
        <div
          className={classNames({
            "py-2": variant !== "multipleSearch" && variant !== "largeSearch",
            "flex min-h-0 flex-col": variant === "multipleSearch",
          })}
        >
          {emptyOptionsPlaceholder &&
            options.length === 0 &&
            !isSearchVariant(variant) &&
            emptyOptionsPlaceholder}
          {variant === "multipleSearch" && (
            <DropdownMultipleSearch
              name={name}
              options={options}
              selectOnCheck={selectOnCheck}
              applyText={applyText}
              emptyOptionsPlaceholder={emptyOptionsPlaceholder}
              closeEvent={closeEvent}
              value={dropdownMultipleValue}
              setValue={setDropdownValue}
              placeholder={_placeholder}
              onApply={onApply}
              onCancel={onCancel}
              menuOpened={menuOpened}
              positionOptionsRelative={positionOptionsRelative}
              positionOptionsRelativeHeight={positionOptionsRelativeHeight}
            />
          )}
          {["normalSearch", "largeSearch"].includes(variant) && (
            <DropdownSearch
              name={name}
              placeholder={_placeholder}
              emptyOptionsPlaceholder={emptyOptionsPlaceholder}
              options={options}
              numVisibleOptions={numVisibleOptions}
              viewMoreOptionsButtonText={viewMoreOptionsButtonText}
              setValue={setDropdownValue}
              value={Array.isArray(dropdownValue) ? undefined : dropdownValue}
              variant={variant}
            />
          )}
          {variant === "multiple" && (
            <DropdownMultiple
              options={options}
              value={dropdownMultipleValue}
              setValue={setDropdownValue}
            />
          )}
          {variant === "normal" &&
            (groupByProp
              ? getObjectEntries(groupBy(options, groupByProp.key)).map(
                  ([group, groupOptions]) => (
                    <Fragment key={`${group}`}>
                      <div className="ml-4 mt-4 w-max border-b border-straps-secondary text-xs text-straps-secondary">
                        {group}
                      </div>
                      {groupOptions.map((option) => (
                        <DropdownOptionButton
                          key={`${option.id}-${group}`}
                          option={option}
                          variant={variant}
                          handleSelect={setDropdownValue}
                          isChecked={isChecked}
                        />
                      ))}
                    </Fragment>
                  )
                )
              : options.map((option) => (
                  <DropdownOptionButton
                    key={`${option.id}`}
                    option={option}
                    variant={variant}
                    handleSelect={setDropdownValue}
                    isChecked={isChecked}
                  />
                )))}
        </div>
      </DropdownWrapper>

      {supportText && (
        <Text
          variant={supportTextVariant ?? "sb_t-14-500"}
          className="pt-2"
          color={supportTextColor}
        >
          {supportText}
        </Text>
      )}
    </div>
  );
};

export default Dropdown;
